'use server';

import { SessionService } from '@/contexts/authentication';
import { getTokenManager } from '@/contexts/subscriptions/services/getTokenManager';
import { walletAccountId } from '@/contexts/subscriptions/services/walletAccountId';
import { BOOK_SIZES, charsForBlocks } from '@/contexts/stories/utils/storyShape';
import { CAST_REFERENCE_LIMITS } from '@/contexts/illustrations/services/BookService';

/**
 * The unit the meter counts in: ONE SMALL BOOK.
 *
 * It used to count whole 28-page stories, and a pool with two thirds of a
 * grant left reported "≈ 1 illustrated story" — true, useless, and read as
 * "almost nothing". A small book is the size a parent actually orders first
 * (and the campaign's own), so "three more small books" is a number they can
 * act on. Images = its pages + the cover; the cast is the drawn references it
 * pays for before a page exists, capped where BookService caps them.
 */
/**
 * The cover is not one more page, and it is not three either.
 *
 * It carries the whole cast as reference images, so its prompt is larger than a
 * page's — but the charge is not. Measured end to end on the campaign book of
 * 2026-10-24 (ledger acc_76938f5a): pages 111-149 credits each, the cover 190.
 * That is 1.6 pages, not the 3 this constant was set to from prompt size alone,
 * and the difference was most of why the meter told a parent with half the
 * bonus left that they could make no more books.
 */
const COVER_WEIGHT = 1.6;
/**
 * What a drawn reference costs, as a fraction of a page.
 *
 * The cast is rendered by the portrait engine. When the admin has selected one,
 * it is priced from the price book directly; when it is left to follow the page
 * model (dev, and any install that never set it) the model id is the same but
 * the render is not — same book: 40 credits a reference against 118 a page.
 * Pricing a reference as a whole page overstated the cast by nearly three
 * pages' worth.
 */
const REFERENCE_SHARE_OF_PAGE = 0.34;
/** Images in a small book, with the cover weighted as what it costs. */
const SMALL_BOOK_IMAGES = BOOK_SIZES.small + COVER_WEIGHT;
/** Narration of a small book ≈ its pages × the age target, in 1k-char units. */
const SMALL_BOOK_1K_CHARS = charsForBlocks(BOOK_SIZES.small, '5-7') / 1000;

export interface AllowanceMeterData {
  /** 0..1 for the meter bar. */
  percent: number;
  nextExpiry: string | null;
  /** Content-unit "≈ N left" estimates (null when unpriced). */
  estimates: {
    /** Whole SMALL books (10 pages + cover + the story text and its analysis). */
    smallBook: number | null;
    illustration: number | null;
    voiceover: number | null;
  };
  /** False = no subscription and no live bonus (show "subscribe" affordance). */
  hasAllowance: boolean;
  /** Live (active/trialing) subscription for this app — picks the wall copy:
   *  false → "subscribe"; true → "plan limit reached, upgrade / refills in N days". */
  hasSubscription: boolean;
}

/**
 * The hidden-credits read for the UI: turns the per-app allowance into a % + a few
 * content-unit estimates. Never returns a raw credit count for display purposes —
 * the component shows %, and the estimates. Null for anonymous / legacy manager.
 */
export async function getAllowanceMeter(): Promise<AllowanceMeterData | null> {
  const familyId = await SessionService.getFamilyId();
  if (!familyId) return null;
  const accountId = await walletAccountId(familyId);

  const tm = getTokenManager() as {
    allowanceSummary?: (a: string) => Promise<{
      remaining: number; held: number; available: number; nextExpiry: string | null; periodGrant: number; hasSubscription: boolean;
    }>;
    estimate?: (i: { op_type: string; provider: string; model: string; units: Record<string, number> }) => Promise<number | null>;
  };
  if (typeof tm.allowanceSummary !== 'function') return null; // IDENTITY_SERVICE off → legacy

  const s = await tm.allowanceSummary(accountId);

  // Per-feature cost-credit charge via the accounts price-book (side-effect-free).
  let illustration: number | null = null;
  let voiceover: number | null = null;
  let bookCost: number | null = null;
  try {
    const { UnifiedAIConfigService } = await import('@/contexts/ai/services/config/UnifiedAIConfigService');
    const cfg = (await UnifiedAIConfigService.getConfig()) as unknown as {
      imagen: { provider: string; model: string };
      tts: { provider: string; standardModel: string };
      llm?: { provider?: string; model?: string };
    };
    const imgCost = (await tm.estimate?.({ op_type: 'image', provider: cfg.imagen.provider, model: cfg.imagen.model, units: { image: 1 } })) ?? null;
    /**
     * A reference is NOT a page.
     *
     * The cast is drawn by the portrait engine (admin: AI config → Imagen →
     * portrait engine), which is a different model at a different price —
     * about a quarter of a page today. Pricing six references as six pages
     * overstated a book by a third, in the one number a parent uses to decide
     * whether their balance is enough.
     */
    const pageShare = imgCost != null ? imgCost * REFERENCE_SHARE_OF_PAGE : null;
    let refCost: number | null = pageShare;
    try {
      const { AIProviderConfigService } = await import('@/contexts/ai/services/config/AIProviderConfigService');
      const portrait = await AIProviderConfigService.getPortraitSelection();
      const quoted = (await tm.estimate?.({
        op_type: 'image',
        provider: portrait.provider,
        model: portrait.model,
        units: { image: 1 },
      })) ?? null;
      // A portrait engine of its own is priced as itself. The same model as the
      // pages is NOT the same render, so that case falls back to the measured
      // share rather than to a whole page.
      refCost =
        quoted != null && (portrait.model !== cfg.imagen.model || portrait.provider !== cfg.imagen.provider)
          ? quoted
          : pageShare;
    } catch {
      refCost = pageShare;
    }
    const voiceCost = (await tm.estimate?.({ op_type: 'tts', provider: cfg.tts.provider, model: cfg.tts.standardModel, units: { '1k_chars': SMALL_BOOK_1K_CHARS } })) ?? null;
    /**
     * The text passes are not free and they are not small: writing a ten-page
     * story and the art-direction bible that follows it is the second-largest
     * line of a book after the pages themselves. Sized from the holds the
     * operations registry was measured into (story 500, analysis 850) read as
     * input/output tokens at the configured model's own price.
     */
    // In its OWN guard: an LLM the price book does not know must cost the
    // parent a less precise book estimate, not all three estimates.
    let textCost: number | null = null;
    try {
      textCost = cfg.llm?.provider
        ? (await tm.estimate?.({
            op_type: 'llm',
            provider: cfg.llm.provider,
            model: cfg.llm.model ?? '',
            // Measured on the campaign books: the story and the art-direction
            // pass together read ~10k in and write ~8k out.
            // Measured on the 2026-10-24 campaign book: the story, the art
            // direction and the page prompts settled at 237 credits together,
            // which at this model's rates is ~20k in and ~18k out.
            units: { '1k_input_tokens': 20, '1k_output_tokens': 18 },
          })) ?? null
        : null;
    } catch {
      textCost = null;
    }
    const avail = s.available;
    illustration = imgCost != null && imgCost > 0 ? Math.floor(avail / imgCost) : null;
    voiceover = voiceCost != null && voiceCost > 0 ? Math.floor(avail / voiceCost) : null;
    bookCost =
      imgCost != null
        ? imgCost * SMALL_BOOK_IMAGES
          + (refCost ?? imgCost * REFERENCE_SHARE_OF_PAGE) * CAST_REFERENCE_LIMITS.living
          + (textCost ?? 0)
        : null;
  } catch {
    /* estimate best-effort — meter still renders % without counts */
  }

  const smallBook = bookCost != null && bookCost > 0 ? Math.floor(s.available / bookCost) : null;
  const percent = s.periodGrant > 0 ? Math.max(0, Math.min(1, s.available / s.periodGrant)) : s.available > 0 ? 1 : 0;

  return {
    percent,
    nextExpiry: s.nextExpiry,
    estimates: { smallBook, illustration, voiceover },
    hasAllowance: s.periodGrant > 0 || s.available > 0,
    hasSubscription: s.hasSubscription,
  };
}
